import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Trash2, DoorOpen, Package, Minus, Lock, ChevronUp, ChevronDown, Sparkles } from "lucide-react";
import { Label } from "@/components/ui/label";

/**
 * Petit rendu SVG d'aperçu poignée pour chaque type LMC.
 * Utilisé dans le picker de la section.
 */
function HandlePreview({ type, color = "#111" }) {
  if (type === "push") {
    return (
      <svg width="28" height="28" viewBox="0 0 28 28">
        <rect x={4} y={5} width={20} height={18} rx={2} fill="none" stroke="currentColor" strokeWidth={1.4} opacity={0.35} />
        <circle cx={14} cy={14} r={1.5} fill="currentColor" opacity={0.55} />
      </svg>
    );
  }
  if (type === "profil_line") {
    return (
      <svg width="28" height="28" viewBox="0 0 28 28">
        <rect x={3} y={6} width={22} height={16} rx={1.5} fill="none" stroke="currentColor" strokeWidth={1} opacity={0.35} />
        <rect x={5} y={8} width={18} height={2.5} rx={1} fill={color} />
      </svg>
    );
  }
  if (type === "etrier_square") {
    return (
      <svg width="28" height="28" viewBox="0 0 28 28">
        <rect x={3} y={4} width={22} height={20} rx={1.5} fill="none" stroke="currentColor" strokeWidth={1} opacity={0.35} />
        <rect x={19} y={9} width={2.5} height={10} rx={0.6} fill={color} />
        <rect x={18.5} y={8} width={3.5} height={2.5} fill={color} />
        <rect x={18.5} y={17.5} width={3.5} height={2.5} fill={color} />
      </svg>
    );
  }
  if (type === "bouton_cyl") {
    return (
      <svg width="28" height="28" viewBox="0 0 28 28">
        <rect x={3} y={4} width={22} height={20} rx={1.5} fill="none" stroke="currentColor" strokeWidth={1} opacity={0.35} />
        <circle cx={20} cy={14} r={3.2} fill={color} stroke="rgba(0,0,0,0.25)" strokeWidth={0.5} />
        <circle cx={19} cy={13} r={0.8} fill="rgba(255,255,255,0.7)" />
      </svg>
    );
  }
  return null;
}

export default function SectionEditor({
  section,
  index,
  breakdown,
  handles,
  fixedShelfRequired = 0,
  onChange,
  onRemove,
  onRemoveElement,
  onMoveElement,
  onToggleTraversingDrawer,
  canRemove = true,
  canSpanDrawer = false,
  heightCm = 200,
  sectionWidthCm = 0,
}) {
  const label = String.fromCharCode(65 + index);
  const secBreakdown = useMemo(
    () => breakdown?.sections?.find((s) => s.id === label),
    [breakdown, label],
  );

  const update = (patch) => onChange({ ...section, ...patch });
  const elements = section.elements || [];
  const elementCounts = elements.reduce((acc, e) => ({ ...acc, [e.kind]: (acc[e.kind] || 0) + 1 }), {});
  const drawersFromEl = elementCounts.drawer || 0;
  const hasHardware = !!section.has_door || drawersFromEl > 0;
  const handleUsed = section.handle_id || "profil_line_alu";
  const doubleDoorRequired = sectionWidthCm * 10 > 600;

  // Regroupement des poignées par gamme LMC
  const handleGroups = useMemo(() => {
    const groups = {};
    handles.forEach((h) => {
      const g = h.gamme || "Autres";
      if (!groups[g]) groups[g] = [];
      groups[g].push(h);
    });
    return groups;
  }, [handles]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      data-testid={`section-editor-${label}`}
      className="paper p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground font-heading text-base flex items-center justify-center">
            {label}
          </span>
          <div>
            <p className="font-heading text-lg leading-tight">Section {label}</p>
            {section.width_cm && (
              <p className="text-[10px] text-muted-foreground tabular-nums">
                {Math.round(section.width_cm * 10)} mm
              </p>
            )}
          </div>
        </div>
        {canRemove && (
          <button
            data-testid={`section-remove-${label}`}
            onClick={onRemove}
            className="w-8 h-8 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors"
            title="Supprimer cette section"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Aménagements */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
          Aménagement
        </Label>
        <div className="space-y-2">
          {/* Porte */}
          <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-background hover:border-primary/40 transition-colors cursor-pointer">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                data-testid={`section-${label}-door`}
                checked={!!section.has_door}
                onChange={(e) => update({ has_door: e.target.checked, door_mirror: e.target.checked ? section.door_mirror : false, door_standard: section.door_standard || "single_right" })}
                className="w-4 h-4 accent-primary"
              />
              <DoorOpen className="w-4 h-4 text-primary" strokeWidth={1.6} />
              <span className="text-sm">Porte / façade</span>
            </div>
            <span className="text-[10px] text-muted-foreground">selon dim.</span>
          </label>

          {/* Porte miroir — option indépendante de la finition globale, propre à cette section */}
          {section.has_door && (
            <label className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-background hover:border-primary/40 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  data-testid={`section-${label}-door-mirror`}
                  checked={!!section.door_mirror}
                  onChange={(e) => update({ door_mirror: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.6} />
                <span className="text-sm">Porte miroir sur cette section</span>
              </div>
            <span className="text-[10px] text-muted-foreground">Finition miroir</span>
            </label>
          )}

          {section.has_door && (
            <div data-testid={`section-${label}-door-standard`} className="rounded-lg border border-border bg-background p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Standard et ouverture</p>
              {doubleDoorRequired && <p data-testid={`section-${label}-door-auto-double`} className="mb-2 text-[10px] text-primary font-medium">Double vantail automatique au-delà de 600 mm.</p>}
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  ["single_left", "1 vantail G"],
                  ["single_right", "1 vantail D"],
                  ["double", "2 vantaux"],
                ].map(([value, name]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`section-${label}-door-standard-${value}`}
                    onClick={() => !doubleDoorRequired && update({ door_standard: value })}
                    disabled={doubleDoorRequired && value !== "double"}
                    className={`min-h-9 px-2 rounded-md border text-[10px] font-medium transition-colors ${((doubleDoorRequired ? "double" : section.door_standard) || "single_right") === value ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"} disabled:opacity-40`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {section.has_door && (
            <div data-testid={`section-${label}-door-coverage`} className="rounded-lg border border-border bg-background p-3 space-y-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Zone de porte</p>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  ["full", "Toute hauteur"],
                  ["top", "Haut seul"],
                  ["bottom", "Bas seul"],
                ].map(([value, name]) => (
                  <button key={value} type="button" data-testid={`section-${label}-door-coverage-${value}`} onClick={() => update({ door_coverage: value, door_height_mm: value === "full" ? null : (section.door_height_mm || Math.round(heightCm * 10 * 0.6)) })} className={`min-h-9 px-2 rounded-md border text-[10px] font-medium transition-colors ${(section.door_coverage || "full") === value ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"}`}>
                    {name}
                  </button>
                ))}
              </div>
              {(section.door_coverage || "full") !== "full" && (
                <label className="block text-xs text-muted-foreground">
                  Hauteur de la porte (mm)
                  <input data-testid={`section-${label}-door-height`} type="number" min="200" max={heightCm * 10} step="10" value={section.door_height_mm || Math.round(heightCm * 10 * 0.6)} onChange={(event) => update({ door_height_mm: Math.max(200, Math.min(heightCm * 10, Number(event.target.value) || 200)) })} className="mt-1.5 h-9 w-full rounded-md border border-border bg-card px-2 text-sm text-foreground" />
                </label>
              )}
            </div>
          )}

          {/* Éléments posés par Drag & Drop */}
          <div className="p-3 rounded-lg border border-dashed border-primary/30 bg-primary/5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-primary" strokeWidth={1.6} />
                <span className="text-xs font-medium">Éléments placés</span>
                <span className="text-[10px] text-muted-foreground">({elements.length})</span>
              </div>
              <span className="text-[10px] text-muted-foreground italic">glisser depuis la palette →</span>
            </div>
            {elements.length === 0 ? (
              <p className="text-[10px] text-muted-foreground italic text-center py-2">
                Glissez tiroirs, penderies ou tablettes depuis la palette dans cette section.
              </p>
            ) : (
              <div className="space-y-1" data-testid={`section-${label}-elements-list`}>
                {[...elements].sort((a, b) => b.y_mm - a.y_mm).map((el) => {
                  const kindLabel = { drawer: "Tiroir", penderie: "Barre penderie", shelf_fixed: el.locked ? "Tab. fixe structurelle" : "Tab. fixe", shelf_mobile: "Tab. mobile" }[el.kind] || el.kind;
                  return (
                    <div
                      key={el.id}
                      data-testid={`element-${el.id}`}
                      className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border text-xs ${el.locked ? "bg-primary/5 border-primary/30" : "bg-background border-border"}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {el.locked ? (
                          <Lock className="w-3 h-3 text-primary shrink-0" />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                        )}
                        <span className="truncate">{kindLabel}</span>
                      </div>
                      {el.locked ? (
                        <>
                          <span className="text-[10px] text-muted-foreground tabular-nums">H : {el.y_mm} mm</span>
                          <span className="text-[9px] uppercase tracking-wider text-primary/70 shrink-0" title="Verrouillé — glissez la tablette directement dans le meuble à gauche">
                            ⇕ glisser
                          </span>
                        </>
                      ) : (
                        <div className="flex items-center gap-1 shrink-0">
                          {el.kind === "drawer" && canSpanDrawer && (
                            <button
                              type="button"
                              data-testid={`toggle-traversing-drawer-${el.id}`}
                              onClick={() => onToggleTraversingDrawer && onToggleTraversingDrawer(index, el.id)}
                              className={`h-6 px-1.5 rounded-md border text-[9px] font-medium transition-colors ${(el.span_sections || 1) > 1 ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-primary hover:border-primary/50"}`}
                              title={(el.span_sections || 1) > 1 ? "Revenir à un tiroir de section" : "Étendre sur la section suivante"}
                            >
                              {(el.span_sections || 1) > 1 ? "Traversant" : "Étirer / Fusionner →"}
                            </button>
                          )}
                          <ElementHeightEditor
                            el={el}
                            maxMm={Math.round(heightCm * 10)}
                            onChangeY={(newY) => onMoveElement && onMoveElement(index, el.id, newY)}
                            onRemove={() => onRemoveElement && onRemoveElement(index, el.id)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Poignée LMC groupée par gamme */}
      {hasHardware && (
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
            Poignée / quincaillerie
          </Label>
          <div className="space-y-3">
            {Object.entries(handleGroups).map(([gamme, list]) => (
              <div key={gamme}>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{gamme}</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {list.map((h) => {
                    const active = handleUsed === h.id;
                    return (
                      <button
                        key={h.id}
                        type="button"
                        data-testid={`section-${label}-handle-${h.id}`}
                        onClick={() => update({ handle_id: h.id })}
                        className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-all ${
                          active ? "border-primary bg-primary/5 shadow-sm" : "border-border hover:border-muted-foreground/40 bg-background"
                        }`}
                        title={h.name}
                      >
                        <div className={active ? "text-primary" : "text-foreground/70"}>
                          <HandlePreview type={h.svg_type} color={h.swatch} />
                        </div>
                        <span className="text-[9px] leading-tight text-center line-clamp-2">{h.finish}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}


/**
 * ElementHeightEditor — édition interactive de la hauteur d'un élément placé.
 *  - Input mm (saisie clavier autorisée à la demande utilisateur)
 *  - Flèches ↑ / ↓ (pas de 10 mm)
 *  - Bouton "−" retirer
 * Le composant maintient un state local pour l'édition libre ; la valeur est
 * commit sur blur / Enter / clic sur les flèches. Le parent applique la
 * collision-detection dans handleMoveElement et peut refuser le changement,
 * auquel cas le state se re-synchronise via useEffect sur el.y_mm.
 */
function ElementHeightEditor({ el, maxMm, onChangeY, onRemove }) {
  const [draft, setDraft] = useState(String(el.y_mm));

  // Resync depuis le parent lorsque le y_mm réel change (après collision fix ou drag).
  useEffect(() => {
    setDraft(String(el.y_mm));
  }, [el.y_mm]);

  const commit = (rawStr) => {
    const parsed = parseInt(rawStr, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(el.y_mm));
      return;
    }
    const clamped = Math.max(0, Math.min(maxMm, parsed));
    if (clamped !== el.y_mm) onChangeY(clamped);
    // Le useEffect resync ensuite avec la vraie valeur appliquée.
  };

  const nudge = (delta) => {
    const next = Math.max(0, Math.min(maxMm, el.y_mm + delta));
    onChangeY(next);
  };

  return (
    <div className="flex items-center gap-1 shrink-0">
      <span className="text-[9px] text-muted-foreground">H</span>
      <input
        data-testid={`element-height-input-${el.id}`}
        type="number"
        inputMode="numeric"
        min={0}
        max={maxMm}
        step={10}
        value={draft}
        onChange={(e) => {
          const val = e.target.value;
          setDraft(val);
          // Live update — chaque frappe applique la position si la valeur est un nombre valide.
          // Le parent applique la collision detection ; si refusée, l'useEffect resync automatiquement.
          const parsed = parseInt(val, 10);
          if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= maxMm) {
            onChangeY(parsed);
          }
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(e.currentTarget.value);
            e.currentTarget.blur();
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            nudge(10);
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            nudge(-10);
          }
        }}
        className="w-14 h-6 px-1 text-[11px] tabular-nums text-right border rounded-md bg-background border-border focus:outline-none focus:ring-2 focus:ring-primary/40"
        aria-label="Hauteur en millimètres"
      />
      <span className="text-[9px] text-muted-foreground">mm</span>
      <button
        type="button"
        data-testid={`element-nudge-up-${el.id}`}
        onClick={() => nudge(10)}
        className="w-5 h-5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 flex items-center justify-center transition-colors"
        title="Monter de 10 mm"
        aria-label="Monter"
      >
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        data-testid={`element-nudge-down-${el.id}`}
        onClick={() => nudge(-10)}
        className="w-5 h-5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 flex items-center justify-center transition-colors"
        title="Descendre de 10 mm"
        aria-label="Descendre"
      >
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        data-testid={`remove-element-${el.id}`}
        onClick={onRemove}
        className="w-5 h-5 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10 flex items-center justify-center transition-colors ml-0.5"
        title="Retirer cet élément"
      >
        <Minus className="w-3 h-3" />
      </button>
    </div>
  );
}
